"use client";
import { Track } from "@/src/lib/tracking";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";

import type {
  OnboardingListItem,
  OnboardingAgentListItem,
  OnboardingCategoryItem,
  AgentWithIds,
} from "./types";
import { getAccentByAgentLabel } from "./accentStyles";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import { AgentStatusEnum, type Agent } from "@features/api/memberApi";
import { I18nUtil } from "@utils/i18n";
import { QUERY_KEYS } from "@config/constant";
import { useOwnerPreference } from "@/src/hooks/useOwnerPreference";

/**
 * @description 将 API 返回的 Agent 转换为组件需要的 Agent 格式
 * @param agent - API 返回的 Agent 对象
 * @returns 转换后的 Agent 数据
 */
const transformApiDataToAgent = (agent: Agent): OnboardingAgentListItem => {
  // 将 categories 转换为全大写，统一格式
  const normalizedCategories = (agent.categories ?? []).map((cat) =>
    cat.toUpperCase(),
  );

  return {
    type: "agent",
    id: agent.id ?? "",
    name: agent.name ?? "",
    label: agent.label ?? "",
    description: agent.description ?? "",
    icon: agent.icon ?? "",
    avatar: agent.avatar ?? "",
    status: agent.status,
    categories: normalizedCategories,
    default_agent: agent.default_agent,
    // 颜色
    accent: getAccentByAgentLabel(agent.label ?? ""),
    autoRotatable: true,
  };
};

/**
 * @description 将多个 agent ids 组合成逗号分隔的字符串
 * @param ids - Agent ID 数组
 * @returns 组合后的 ID 字符串，如 "sentiment,sentiment2"
 */
const combineAgentIds = (ids: string[]): string => {
  return ids.join(",");
};

/**
 * @description 合并 categories 数组，将 ["STOCK"] 和 ["CRYPTO"] 合并为 ["~STOCK & CRYPTO~"]
 * @param categoriesList - Categories 数组列表
 * @returns 合并后的 categories 数组
 */
const mergeCategories = (categoriesList: string[][]): string[] => {
  // 收集所有唯一的 category
  const allCategories = new Set<string>();
  categoriesList.forEach((categories) => {
    categories.forEach((cat) => allCategories.add(cat));
  });

  // 检查是否有 Stocks 和 Crypto 的组合
  const hasStocks = allCategories.has("STOCK");
  const hasCrypto = allCategories.has("CRYPTO");

  if (hasStocks && hasCrypto) {
    // 如果有 Stocks 和 Crypto，合并为 "Stocks + Crypto"
    const result = Array.from(allCategories).filter(
      (cat) => cat !== "STOCK" && cat !== "CRYPTO",
    );
    result.push("STOCK & CRYPTO");
    return result;
  }

  // 否则返回所有 categories
  return Array.from(allCategories);
};

/**
 * @description 检查一组 agents 是否可以聚合（Stocks + Crypto 组合）
 * @param agents - 相同 name 的 agents 数组
 * @returns 是否可以聚合
 */
const canAggregate = (agents: OnboardingAgentListItem[]): boolean => {
  if (agents.length < 2) {
    return false;
  }

  // 收集所有 categories
  const allCategories = new Set<string>();
  agents.forEach((agent) => {
    if (agent.categories && agent.categories.length > 0) {
      agent.categories.forEach((cat) => allCategories.add(cat));
    }
  });

  // 检查是否有 Stocks 和 Crypto，且每个 agent 只有一个 category
  const hasStocks = allCategories.has("STOCK");
  const hasCrypto = allCategories.has("CRYPTO");

  if (!hasStocks || !hasCrypto) {
    return false;
  }

  // 确保每个 agent 只有一个 category，且分别是 Stocks 和 Crypto
  const agentsWithSingleCategory = agents.filter(
    (agent) => agent.categories && agent.categories.length === 1,
  );

  if (agentsWithSingleCategory.length !== agents.length) {
    return false;
  }

  // 检查是否有分别包含 Stocks 和 Crypto 的 agents
  const hasStocksOnly = agents.some(
    (agent) =>
      agent.categories?.length === 1 && agent.categories[0] === "STOCK",
  );
  const hasCryptoOnly = agents.some(
    (agent) =>
      agent.categories?.length === 1 && agent.categories[0] === "CRYPTO",
  );

  return hasStocksOnly && hasCryptoOnly;
};

/**
 * @description 根据 name 分组 agents
 * @param agents - Agent 列表
 * @returns 按 name 分组的 Map
 */
const groupAgentsByName = (
  agents: OnboardingAgentListItem[],
): Map<string, OnboardingAgentListItem[]> => {
  const groups = new Map<string, OnboardingAgentListItem[]>();

  agents.forEach((agent) => {
    const name = agent.name;
    if (!groups.has(name)) {
      groups.set(name, []);
    }
    groups.get(name)!.push(agent);
  });

  return groups;
};

/**
 * @description 处理 agent 聚合逻辑
 * @param agents - Agent 列表
 * @returns 处理后的 Agent 列表（已聚合的 agents）
 */
const processAgentAggregation = (
  agents: OnboardingAgentListItem[],
): OnboardingAgentListItem[] => {
  const groups = groupAgentsByName(agents);
  const processedNames = new Set<string>();
  const result: OnboardingAgentListItem[] = [];

  // 按照原始 agents 数组的顺序处理，以保持顺序
  agents.forEach((agent) => {
    const name = agent.name;
    if (processedNames.has(name)) {
      // 这个 name 已经处理过了，跳过
      return;
    }

    const groupAgents = groups.get(name);
    if (!groupAgents) {
      return;
    }

    processedNames.add(name);

    if (canAggregate(groupAgents)) {
      // 可以聚合，创建一个聚合 agent
      // 按照原始顺序排序，确保第一个 agent 是最先出现的
      const sortedGroupAgents = groupAgents.sort((a, b) => {
        const indexA = agents.findIndex((ag) => ag.id === a.id);
        const indexB = agents.findIndex((ag) => ag.id === b.id);
        return indexA - indexB;
      });

      const firstAgent = sortedGroupAgents[0];
      const allIds = sortedGroupAgents.map((ag) => ag.id);
      const allCategories = sortedGroupAgents
        .map((ag) => ag.categories || [])
        .filter((cats) => cats.length > 0);

      const aggregatedAgent: OnboardingAgentListItem = {
        ...firstAgent,
        id: combineAgentIds(allIds),
        ids: allIds,
        categories: mergeCategories(allCategories),
      };

      result.push(aggregatedAgent);
    } else {
      // 不能聚合，保持原样，但需要按照原始顺序添加
      const sortedGroupAgents = groupAgents.sort((a, b) => {
        const indexA = agents.findIndex((ag) => ag.id === a.id);
        const indexB = agents.findIndex((ag) => ag.id === b.id);
        return indexA - indexB;
      });
      result.push(...sortedGroupAgents);
    }
  });

  return result;
};

/**
 * @description 根据 categories 自动生成分组列表
 * @param agents - Agent 列表
 * @returns 包含 category 和 agent 的有序列表
 */
const generateListWithCategories = (
  agents: OnboardingAgentListItem[],
): OnboardingListItem[] => {
  // 收集所有唯一的 category，按照在 agents 数组中首次出现的顺序
  const categoryOrder: string[] = [];
  const categorySet = new Set<string>();

  agents.forEach((agent) => {
    if (agent.categories && agent.categories.length > 0) {
      agent.categories.forEach((cat) => {
        if (!categorySet.has(cat)) {
          categorySet.add(cat);
          categoryOrder.push(cat);
        }
      });
    }
  });

  // 构建结果列表
  const result: OnboardingListItem[] = [];

  // 首先添加没有 category 的 agent（保持原始顺序）
  const agentsWithoutCategory = agents.filter(
    (agent) => !agent.categories || agent.categories.length === 0,
  );
  result.push(...agentsWithoutCategory);

  // 将 "~STOCK & CRYPTO~" 从 categoryOrder 中分离出来，优先处理
  const stockCryptoIndex = categoryOrder.indexOf("STOCK & CRYPTO");
  const stockCryptoCategory =
    stockCryptoIndex !== -1 ? categoryOrder[stockCryptoIndex] : null;
  const otherCategories = categoryOrder.filter(
    (cat) => cat !== "STOCK & CRYPTO",
  );

  // 优先添加 "~STOCK & CRYPTO~" 分组
  if (stockCryptoCategory) {
    const categoryItem: OnboardingCategoryItem = {
      type: "category",
      id: stockCryptoCategory.toLowerCase().replace(/\s+/g, "-"),
      label: stockCryptoCategory,
    };
    result.push(categoryItem);

    // 添加属于该 category 的 agent（保持原始顺序）
    const agentsInCategory = agents.filter(
      (agent) =>
        agent.categories && agent.categories.includes(stockCryptoCategory),
    );
    result.push(...agentsInCategory);
  }

  // 然后按 category 首次出现的顺序分组添加其他 category
  otherCategories.forEach((category) => {
    // 添加 category 项
    const categoryItem: OnboardingCategoryItem = {
      type: "category",
      id: category.toLowerCase().replace(/\s+/g, "-"),
      label: category,
    };
    result.push(categoryItem);

    // 添加属于该 category 的 agent（保持原始顺序）
    const agentsInCategory = agents.filter(
      (agent) => agent.categories && agent.categories.includes(category),
    );
    result.push(...agentsInCategory);
  });

  return result;
};

export const useOnboardingData = (
  /** 是否只显示 default_agent 的 agent */
  showOnlyDefaultAgent: boolean = false,
  /** 是否通过订阅状态过滤 */
  filterBySubscription: boolean = false,
  /** 服务端获取的初始 Agent 数据 */
  initialAgentsData?: Agent[],
) => {
  const { memberApi } = useContext(OspApiClientContext);
  const params = useParams<{ locale: string }>();
  const locale = params.locale as string;

  // 调试日志：检查 memberApi 是否可用
  useEffect(() => {
    if (!memberApi) {
    }
  }, [memberApi, showOnlyDefaultAgent, filterBySubscription]);

  // 获取用户偏好（filterBySubscription 为 false 时跳过）
  const { ownerPreference } = useOwnerPreference({
    enabled: filterBySubscription,
  });

  // 从 ownerPreference 中提取 subscribedAgentIds，保持向后兼容
  const subscribedAgentIds = useMemo(() => {
    return ownerPreference?.agent_ids || [];
  }, [ownerPreference]);

  /**
   * @description 是否有记录过风险投资偏好
   */
  const hasRecordRiskLevel = useMemo(() => {
    return !!ownerPreference?.risk_level;
  }, [ownerPreference]);

  // 从 API 获取 agents 数据
  const {
    data: apiAgentsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.ONBOARDING_AGENTS,
      locale,
      !!memberApi,
      showOnlyDefaultAgent,
    ],
    enabled: !!memberApi,
    staleTime: 5 * 60 * 1000, // 5分钟缓存
    retry: 2, // 失败时最多重试2次
    placeholderData: initialAgentsData, // 使用服务端获取的初始数据作为占位数据
    queryFn: async () => {
      try {
        const { data }: any = await memberApi?.agents?.listAgent({
          language: I18nUtil.getBackgroundLanguageKey(locale),
        });
        if (data?.data && Array.isArray(data.data)) {
          return data.data as Agent[];
        }
        return [];
      } catch (error) {
        Track.error(error, { tag: "useOnboardingData", msg: "API error" });
        return [];
      }
    },
  });

  // 选择使用的数据源：优先使用 API 数据，失败时回退到硬编码数据
  const agentsApiData = useMemo(() => {
    // 如果 API 调用成功且有数据，使用 API 数据
    if (!isError && apiAgentsData && apiAgentsData.length > 0) {
      return apiAgentsData;
    }
    return [];
  }, [apiAgentsData, isError]);

  // 过滤启用的 agent（status === ACTIVE），然后转换为组件需要的格式
  const transformedAgents = useMemo(() => {
    let agents = agentsApiData
      .filter((agent) => agent.status === AgentStatusEnum.ACTIVE)
      .map(transformApiDataToAgent);

    // 如果开启了订阅过滤，且获取到了订阅列表，则进行过滤
    if (
      filterBySubscription &&
      subscribedAgentIds &&
      subscribedAgentIds.length > 0
    ) {
      agents = agents.filter((agent) => subscribedAgentIds.includes(agent.id));
    }

    return agents;
  }, [agentsApiData, filterBySubscription, subscribedAgentIds]);

  // 处理 agent 聚合逻辑
  const enabledAgents = useMemo(() => {
    return processAgentAggregation(transformedAgents);
  }, [transformedAgents]);

  // 将 default_agent 排到前面
  const sortedAgents = useMemo(() => {
    return [...enabledAgents].sort((a, b) => {
      // default_agent === true 的排在前面
      if (a.default_agent === true && b.default_agent !== true) {
        return -1;
      }
      if (a.default_agent !== true && b.default_agent === true) {
        return 1;
      }
      // 如果 default_agent 状态相同，保持原有顺序
      return 0;
    });
  }, [enabledAgents]);

  // 筛选 default_agent（如果需要）
  const filteredAgents = useMemo(() => {
    if (!showOnlyDefaultAgent) {
      return sortedAgents;
    }
    const defaultAgents = sortedAgents.filter(
      (agent) => agent.default_agent === true,
    );
    // 如果筛选后没有 agent，回退到显示所有 agent，避免空数据错误
    if (defaultAgents.length === 0 && sortedAgents.length > 0) {
    }
    return defaultAgents.length > 0 ? defaultAgents : sortedAgents;
  }, [sortedAgents, showOnlyDefaultAgent]);

  // 根据 categories 自动生成列表
  const listItems = useMemo(
    () => generateListWithCategories(filteredAgents),
    [filteredAgents],
  );

  const agents = useMemo(
    () => filteredAgents.filter((item) => item.autoRotatable ?? true),
    [filteredAgents],
  );

  /**
   * @description Get agents that match the specified risk level (filter by risk_level_scope)
   * @param riskLevel - Risk level (1=NEWBIE, 2=CONSERVATIVE, 3=BALANCED, 4=ADVANCED, 5=AGGRESSIVE, 6=SHORT-TERM)
   * @returns Filtered agents array
   */
  const getAgentsByRiskLevel = useCallback(
    (riskLevel: number): AgentWithIds[] => {
      if (
        hasRecordRiskLevel ||
        !apiAgentsData ||
        riskLevel < 1 ||
        riskLevel > 6
      ) {
        return [];
      }

      const agents = apiAgentsData.filter((agent) =>
        agent.risk_level_scope?.includes(riskLevel),
      );
      const agentsMap = agents.reduce<Record<string, Agent[]>>((acc, agent) => {
        if (!agent?.name) {
          return acc;
        }
        acc[agent.name] = acc[agent.name] || [];
        acc[agent.name].push(agent);
        return acc;
      }, {});

      const result = Object.values(agentsMap).reduce<AgentWithIds[]>(
        (acc, agents) => {
          const categories = agents.flatMap((agent) => agent.categories ?? []);
          const ids = agents.map((agent) => agent.id ?? "");
          const tag = categories
            .map(
              (category) =>
                category.charAt(0).toUpperCase() +
                category.slice(1).toLowerCase(),
            )
            .join("+");
          if (agents.length === 1) {
            acc.push({
              ...agents[0],
              ids,
              tag,
            });
          } else {
            // 产品需求，以STOCK的agent信息为准
            const agent = agents.find((agent) =>
              agent.categories?.includes("STOCK"),
            );
            acc.push({
              ...(agent ?? agents[0]),
              categories,
              tag,
              ids,
            });
          }
          return acc;
        },
        [],
      );

      return result;
    },
    [hasRecordRiskLevel, apiAgentsData],
  );

  // 调试日志：记录最终的数据状态
  useEffect(() => {
    if (!isLoading) {
      if (listItems.length === 0 && !isLoading) {
      }
    }
  }, [
    isLoading,
    listItems.length,
    agents.length,
    filteredAgents.length,
    showOnlyDefaultAgent,
    filterBySubscription,
    subscribedAgentIds,
    apiAgentsData,
    agentsApiData.length,
    transformedAgents.length,
    enabledAgents.length,
  ]);

  return {
    /** 左侧列表数据 */
    listItems,
    /** Agent 列表 */
    agents,
    /** 是否正在加载 */
    isLoading,
    /** API 返回的原始 agents 数据 */
    apiAgentsData,
    /** 是否有记录过风险投资偏好 */
    hasRecordRiskLevel,
    /** 根据风险等级获取匹配的 agents */
    getAgentsByRiskLevel,
  };
};
